import React, { useState, useContext, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TrendingUp,
  LogOut,
  History,
  CreditCard,
  Shield,
  Clock,
  AlertTriangle,
  ChevronRight,
  Loader2,
  Settings,
  RefreshCw,
  Lock,
  TrendingDown,
  Gauge,
  CircleDot,
  CheckCircle2,
  XCircle,
  Timer,
  X,
  Zap,
  Gift,
  Copy,
  Key,
  Eye,
  EyeOff
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, AuthContext } from "@/App";

const INSTRUMENTS = [
  { value: "XAUUSD", label: "XAUUSD", color: "text-amber-500" },
  { value: "XAGUSD", label: "XAGUSD", color: "text-amber-500" },
  { value: "EURUSD", label: "EURUSD", color: "text-white" },
  { value: "AUDUSD", label: "AUDUSD", color: "text-white" },
  { value: "USDJPY", label: "USDJPY", color: "text-white" }
];

// Free users: 1H, 30M only
// Paid users: 5M, 15M, 30M, 1H
const FREE_TIMEFRAMES = [
  { value: "1H", label: "1 Hour" },
  { value: "30M", label: "30 Minutes" }
];

const PAID_TIMEFRAMES = [
  { value: "1H", label: "1 Hour" },
  { value: "30M", label: "30 Minutes" },
  { value: "15M", label: "15 Minutes", paid: true },
  { value: "5M", label: "5 Minutes", paid: true }
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);
  
  const [instrument, setInstrument] = useState("XAUUSD");
  const [timeframe, setTimeframe] = useState("1H");
  const [riskMode, setRiskMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signal, setSignal] = useState(null);
  const [newsEvents, setNewsEvents] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  
  // Discount popup state
  const [showDiscountPopup, setShowDiscountPopup] = useState(false);
  const [discountTimeLeft, setDiscountTimeLeft] = useState(600); // 10 minutes in seconds
  const [discountExpired, setDiscountExpired] = useState(false);
  const [showToken, setShowToken] = useState(false);

  const isPaid = user?.subscription_status === "active";
  const timeframes = isPaid ? PAID_TIMEFRAMES : FREE_TIMEFRAMES;

  // Check if should show discount popup (trials = 0 and not paid)
  useEffect(() => {
    const hasSeenPopup = localStorage.getItem('discount_popup_shown');
    const popupExpiry = localStorage.getItem('discount_popup_expiry');
    
    if (user && user.free_trials_remaining === 0 && !isPaid && !hasSeenPopup) {
      setShowDiscountPopup(true);
      const expiryTime = Date.now() + 600000; // 10 minutes from now
      localStorage.setItem('discount_popup_expiry', expiryTime.toString());
    } else if (popupExpiry && Date.now() < parseInt(popupExpiry)) {
      // Resume timer if still valid
      const remaining = Math.floor((parseInt(popupExpiry) - Date.now()) / 1000);
      if (remaining > 0 && user?.free_trials_remaining === 0 && !isPaid) {
        setDiscountTimeLeft(remaining);
        setShowDiscountPopup(true);
      }
    }
  }, [user, isPaid]);

  // Countdown timer for discount
  useEffect(() => {
    if (!showDiscountPopup || discountExpired) return;
    
    const timer = setInterval(() => {
      setDiscountTimeLeft(prev => {
        if (prev <= 1) {
          setDiscountExpired(true);
          localStorage.setItem('discount_popup_shown', 'true');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showDiscountPopup, discountExpired]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const closeDiscountPopup = () => {
    setShowDiscountPopup(false);
    localStorage.setItem('discount_popup_shown', 'true');
  };

  const claimDiscount = () => {
    if (!discountExpired) {
      navigate('/subscription?discount=30');
    }
    closeDiscountPopup();
  };

  useEffect(() => {
    fetchNews(instrument);
  }, [instrument]);

  const fetchNews = async (inst, forceRefresh = false) => {
    setNewsLoading(true);
    try {
      const url = forceRefresh 
        ? `${API}/market/news?instrument=${inst}&refresh=${Date.now()}`
        : `${API}/market/news?instrument=${inst}`;
      const response = await axios.get(url);
      setNewsEvents(response.data.events || []);
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setNewsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const generateSignal = async () => {
    if (user?.subscription_status === "free" && user?.free_trials_remaining <= 0) {
      toast.error("No free trials remaining. Please subscribe.");
      navigate("/subscription");
      return;
    }

    setLoading(true);
    setSignal(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/signals/generate`,
        {
          instrument,
          bias_tf: timeframe,
          risk_mode: riskMode
        },
        {
          
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      setSignal(response.data);

      // Update user's trial count
      if (user?.subscription_status === "free" && user?.free_trials_remaining > 0) {
        setUser({
          ...user,
          free_trials_remaining: user.free_trials_remaining - 1
        });
      }

      toast.success("Signal generated!");
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error("No free trials remaining. Please subscribe.");
        navigate("/subscription");
      } else {
        toast.error(error.response?.data?.detail || "Failed to generate signal");
      }
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return "—";
    return price.toFixed(instrument === "USDJPY" ? 3 : 5);
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 75) return "text-green-500";
    if (confidence >= 50) return "text-amber-500";
    return "text-red-500";
  };

  const getConfidenceBg = (confidence) => {
    if (confidence >= 75) return "bg-green-500";
    if (confidence >= 50) return "bg-amber-500";
    return "bg-red-500";
  };

  const getSignalTypeLabel = (signalType) => {
    switch (signalType) {
      case "TRADE":
        return { label: "HIGH CONFIDENCE TRADE", color: "bg-green-600" };
      case "CAUTION_TRADE":
        return { label: "PROCEED WITH CAUTION", color: "bg-amber-600" };
      case "NO_TRADE":
        return { label: "NOT RECOMMENDED", color: "bg-red-600" };
      default:
        return { label: signalType, color: "bg-secondary" };
    }
  };

  const getEntryStatusConfig = (status) => {
    switch (status) {
      case "triggered_positive":
        return { 
          icon: CheckCircle2, 
          color: "text-green-400", 
          bg: "bg-green-500/10 border-green-500/30",
          label: "ENTRY TAKEN - IN PROFIT"
        };
      case "triggered_stopped":
        return { 
          icon: XCircle, 
          color: "text-red-400", 
          bg: "bg-red-500/10 border-red-500/30",
          label: "ENTRY TAKEN - STOPPED OUT"
        };
      case "triggered":
        return { 
          icon: CircleDot, 
          color: "text-amber-400", 
          bg: "bg-amber-500/10 border-amber-500/30",
          label: "ENTRY TRIGGERED"
        };
      case "missed":
        return { 
          icon: XCircle, 
          color: "text-red-400", 
          bg: "bg-red-500/10 border-red-500/30",
          label: "ENTRY MISSED"
        };
      case "pending":
      default:
        return { 
          icon: Timer, 
          color: "text-blue-400", 
          bg: "bg-blue-500/10 border-blue-500/30",
          label: "AWAITING ENTRY"
        };
    }
  };

  const getSessionColor = (sessionName) => {
    switch (sessionName?.toLowerCase()) {
      case "asian":
        return "text-purple-400";
      case "london":
        return "text-blue-400";
      case "new york":
        return "text-green-400";
      default:
        return "text-muted-foreground";
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* 30% Discount Popup */}
      {showDiscountPopup && !isPaid && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="relative bg-card border-2 border-green-500 p-8 max-w-md mx-4 animate-in fade-in zoom-in duration-300">
            {/* Close button */}
            <button 
              onClick={closeDiscountPopup}
              className="absolute top-4 right-4 text-muted-foreground hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Gift icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                <Gift className="w-8 h-8 text-green-500" />
              </div>
            </div>

            {/* Content */}
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2">Wait! Special Offer 🎁</h2>
              <p className="text-muted-foreground mb-4">
                Your free trials are exhausted, but we have a special deal for you!
              </p>

              {/* Discount box */}
              <div className="bg-green-500/10 border border-green-500/30 p-4 mb-4">
                <div className="text-4xl font-bold text-green-400 mb-1">30% OFF</div>
                <div className="text-sm text-muted-foreground">Your first monthly subscription</div>
                <div className="mt-2">
                  <span className="text-xl text-muted-foreground line-through mr-2">$30</span>
                  <span className="text-2xl font-bold text-white">$21</span>
                </div>
              </div>

              {/* Timer */}
              {!discountExpired ? (
                <div className="mb-4">
                  <div className="text-sm text-muted-foreground mb-1">Offer expires in:</div>
                  <div className="flex items-center justify-center gap-2">
                    <Timer className="w-5 h-5 text-red-400 animate-pulse" />
                    <span className="text-2xl font-mono font-bold text-red-400">
                      {formatTime(discountTimeLeft)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="mb-4 p-2 bg-red-500/10 border border-red-500/30">
                  <span className="text-red-400">⏰ Offer expired!</span>
                </div>
              )}

              {/* CTA Button */}
              <Button
                onClick={claimDiscount}
                disabled={discountExpired}
                className={`w-full h-12 rounded-none uppercase tracking-wider font-bold ${
                  discountExpired 
                    ? "bg-muted text-muted-foreground cursor-not-allowed" 
                    : "bg-green-500 text-black hover:bg-green-400"
                }`}
              >
                {discountExpired ? "Offer Expired" : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Claim 30% Off Now
                  </>
                )}
              </Button>

              <p className="text-xs text-muted-foreground mt-3">
                This offer won't be shown again
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="glass sticky top-0 z-50 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2">
              <TrendingUp className="w-7 h-7" />
              <span className="text-lg font-bold tracking-tight">Aurex AI</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-1">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-white">
                  Dashboard
                </Button>
              </Link>
              <Link to="/history">
                <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-white">
                  <History className="w-4 h-4 mr-2" />
                  History
                </Button>
              </Link>
              {user?.role === "admin" && (
                <Link to="/admin">
                  <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-white">
                    <Settings className="w-4 h-4 mr-2" />
                    Admin
                  </Button>
                </Link>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Subscription Status */}
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-secondary rounded-none border border-border">
              {isPaid ? (
                <>
                  <Shield className="w-4 h-4 text-green-500" />
                  <span className="text-sm font-mono">PRO</span>
                </>
              ) : (
                <>
                  <span className="text-sm text-muted-foreground">Trials:</span>
                  <span className="font-mono font-bold" data-testid="trials-remaining">
                    {user?.free_trials_remaining || 0}
                  </span>
                </>
              )}
            </div>

            <Link to="/subscription">
              <Button variant="ghost" size="sm" data-testid="subscription-btn">
                <CreditCard className="w-4 h-4" />
              </Button>
            </Link>

            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        <div className="grid lg:grid-cols-12 gap-6">
          
          {/* Left Panel - Controls */}
          <div className="lg:col-span-3 space-y-6">
            {/* Instrument Selection */}
            <div className="bg-card border border-border p-6 space-y-4">
              <h3 className="font-bold text-sm uppercase tracking-wider text-muted-foreground">
                Instrument
              </h3>
              <Select value={instrument} onValueChange={setInstrument}>
                <SelectTrigger 
                  className="h-14 rounded-none border-border bg-background font-mono text-lg"
                  data-testid="instrument-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INSTRUMENTS.map((inst) => (
                    <SelectItem key={inst.value} value={inst.value}>
                      <span className={inst.color}>{inst.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Timeframe Selection */}
            <div className="bg-card border border-border p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-sm uppercase tracking-wider text-muted-foreground">
                  Structure Timeframe
                </h3>
                {!isPaid && (
                  <span className="text-xs text-amber-500 flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Pro
                  </span>
                )}
              </div>
              <Select value={timeframe} onValueChange={setTimeframe}>
                <SelectTrigger 
                  className="h-12 rounded-none border-border bg-background font-mono"
                  data-testid="timeframe-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timeframes.map((tf) => (
                    <SelectItem key={tf.value} value={tf.value} disabled={tf.paid && !isPaid}>
                      <span className="flex items-center gap-2">
                        {tf.label}
                        {tf.paid && !isPaid && <Lock className="w-3 h-3 text-muted-foreground" />}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isPaid && (
                <p className="text-xs text-muted-foreground">
                  5M & 15M available with Pro subscription
                </p>
              )}
            </div>

            {/* Risk Mode Toggle */}
            <div className="bg-card border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="risk-mode" className="font-bold text-sm">Risk Mode</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Allow risky entries if RR ≥ 2.0
                  </p>
                </div>
                <Switch
                  id="risk-mode"
                  checked={riskMode}
                  onCheckedChange={setRiskMode}
                  data-testid="risk-mode-toggle"
                />
              </div>
            </div>

            {/* Generate Button */}
            <Button
              onClick={generateSignal}
              disabled={loading}
              className="w-full h-14 bg-white text-black hover:bg-gray-200 rounded-none uppercase tracking-wider font-bold"
              data-testid="generate-signal-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  Generate Signal
                  <ChevronRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>

            {/* EA API Token - Pro users only */}
            {isPaid && (
              <div className="bg-card border border-border p-4 space-y-3" data-testid="ea-token-section">
                <div className="flex items-center gap-2">
                  <Key className="w-4 h-4 text-amber-500" />
                  <h3 className="font-bold text-xs uppercase tracking-wider text-muted-foreground">
                    EA API Token
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-background border border-border px-3 py-2 font-mono text-xs overflow-hidden text-ellipsis whitespace-nowrap">
                    {showToken ? (localStorage.getItem("token") || "—") : "••••••••••••••••••••"}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 shrink-0"
                    onClick={() => setShowToken(!showToken)}
                    data-testid="toggle-token-btn"
                  >
                    {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 shrink-0"
                    onClick={() => {
                      navigator.clipboard.writeText(localStorage.getItem("token") || "");
                      toast.success("API Token copied!");
                    }}
                    data-testid="copy-token-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Paste this token into your MT5 Expert Advisor</p>
              </div>
            )}

            {/* News Events */}
            <div className="bg-card border border-border p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-sm uppercase tracking-wider text-muted-foreground">
                  Upcoming News
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => fetchNews(instrument, true)}
                  className="h-8 w-8 p-0"
                  disabled={newsLoading}
                  data-testid="refresh-news-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${newsLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <div className="space-y-3">
                {newsLoading ? (
                  <p className="text-sm text-muted-foreground">Loading news...</p>
                ) : newsEvents.length > 0 ? (
                  newsEvents.slice(0, 3).map((event, index) => (
                    <div key={index} className="p-3 bg-secondary/50 border border-border">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-2 h-2 rounded-full ${
                          event.impact === "high" ? "bg-red-500" :
                          event.impact === "medium" ? "bg-amber-500" : "bg-green-500"
                        }`}></span>
                        <span className="font-mono text-xs text-muted-foreground">
                          {event.currency}
                        </span>
                      </div>
                      <p className="text-sm font-medium">{event.event}</p>
                      <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {new Date(event.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No upcoming events</p>
                )}
              </div>
            </div>
          </div>

          {/* Right Panel - Signal Display */}
          <div className="lg:col-span-9">
            {signal ? (
              <div className={`bg-card border ${
                signal.signal_type === "TRADE" 
                  ? signal.direction === "BUY" ? "border-green-500/50 glow-buy" : "border-red-500/50 glow-sell"
                  : signal.signal_type === "CAUTION_TRADE"
                    ? "border-amber-500/50"
                    : "border-border"
              } p-8 animate-slide-up`}>
                
                {/* Header with Direction and Confidence */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <span className={`px-4 py-2 font-bold uppercase tracking-wider text-white ${
                      signal.direction === "BUY" ? "bg-green-600" : "bg-red-600"
                    }`}>
                      {signal.direction === "BUY" ? (
                        <span className="flex items-center gap-2">
                          <TrendingUp className="w-5 h-5" />
                          BUY
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <TrendingDown className="w-5 h-5" />
                          SELL
                        </span>
                      )}
                    </span>
                    <span className={`text-3xl font-bold ${
                      instrument.includes("XA") ? "text-amber-500" : ""
                    }`}>
                      {signal.instrument}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-muted-foreground">Bias TF</span>
                    <div className="font-mono font-bold">{signal.bias_tf}</div>
                  </div>
                </div>

                {/* Session and Entry Status Bar */}
                <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-secondary/20 border border-border">
                  {/* Current Session */}
                  {signal.session && signal.session.current_sessions && (
                    <div className="flex items-center gap-2" data-testid="current-session">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Session:</span>
                      <div className="flex items-center gap-2">
                        {signal.session.current_sessions.map((sess, idx) => (
                          <span 
                            key={idx} 
                            className={`font-bold ${getSessionColor(sess.name)}`}
                          >
                            {sess.name}
                            {sess.status === "active" && (
                              <span className="ml-1 inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                            )}
                          </span>
                        ))}
                      </div>
                      {signal.session.is_overlap && (
                        <span className="text-xs text-amber-400 ml-2">(Overlap)</span>
                      )}
                    </div>
                  )}

                  {/* Entry Status */}
                  {signal.entry_status && (
                    <div className={`flex items-center gap-2 px-3 py-1.5 border ${getEntryStatusConfig(signal.entry_status).bg}`} data-testid="entry-status">
                      {(() => {
                        const config = getEntryStatusConfig(signal.entry_status);
                        const IconComponent = config.icon;
                        return (
                          <>
                            <IconComponent className={`w-4 h-4 ${config.color}`} />
                            <span className={`text-sm font-bold ${config.color}`}>
                              {config.label}
                            </span>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Entry Status Message */}
                {signal.entry_status_message && (
                  <div className={`mb-6 p-3 border ${getEntryStatusConfig(signal.entry_status).bg}`} data-testid="entry-status-message">
                    <p className="text-sm font-medium">{signal.entry_status_message}</p>
                  </div>
                )}

                {/* Confidence Meter */}
                <div className="mb-6 p-4 bg-secondary/30 border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Gauge className="w-5 h-5 text-muted-foreground" />
                      <span className="text-sm font-bold uppercase tracking-wider">Confidence Level</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-2xl font-mono font-bold ${getConfidenceColor(signal.confidence)}`}>
                        {signal.confidence}%
                      </span>
                      <span className={`px-2 py-1 text-xs font-bold uppercase ${getConfidenceBg(signal.confidence)} text-white`}>
                        {signal.confidence_label}
                      </span>
                    </div>
                  </div>
                  <Progress 
                    value={signal.confidence} 
                    className="h-3"
                  />
                  <div className="mt-2">
                    <span className={`text-sm ${getSignalTypeLabel(signal.signal_type).color} text-white px-2 py-1`}>
                      {getSignalTypeLabel(signal.signal_type).label}
                    </span>
                  </div>
                </div>

                {/* Price Levels - Always shown */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-secondary/50 border border-border">
                    <div className="text-xs text-muted-foreground mb-2">ENTRY</div>
                    <div className="font-mono text-2xl font-bold" data-price data-testid="entry-price">
                      {formatPrice(signal.entry)}
                    </div>
                  </div>
                  <div className="p-4 bg-red-500/10 border border-red-500/30">
                    <div className="text-xs text-red-400 mb-2">STOP LOSS</div>
                    <div className="font-mono text-2xl font-bold text-red-400" data-price data-testid="sl-price">
                      {formatPrice(signal.stop_loss)}
                    </div>
                  </div>
                  <div className="p-4 bg-green-500/10 border border-green-500/30 col-span-2 md:col-span-1 lg:col-span-2">
                    <div className="text-xs text-green-400 mb-2">TAKE PROFITS</div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <div className="text-xs text-muted-foreground">TP1</div>
                        <div className="font-mono text-lg font-bold text-green-400" data-price data-testid="tp1-price">
                          {formatPrice(signal.tp1)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">TP2</div>
                        <div className="font-mono text-lg font-bold text-green-400" data-price data-testid="tp2-price">
                          {formatPrice(signal.tp2)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">TP3</div>
                        <div className="font-mono text-lg font-bold text-green-400" data-price data-testid="tp3-price">
                          {formatPrice(signal.tp3)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* RR Ratio */}
                {signal.rr_ratio && (
                  <div className="mb-6 p-3 bg-secondary/30 border border-border inline-block">
                    <span className="text-xs text-muted-foreground mr-2">Risk-Reward Ratio:</span>
                    <span className={`font-mono font-bold ${signal.rr_ratio >= 1.5 ? 'text-green-400' : signal.rr_ratio >= 1 ? 'text-amber-400' : 'text-red-400'}`}>
                      1:{signal.rr_ratio}
                    </span>
                  </div>
                )}

                {/* Why Section */}
                {signal.why && signal.why.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-bold text-sm uppercase tracking-wider text-muted-foreground mb-3">
                      ANALYSIS
                    </h4>
                    <ul className="space-y-2">
                      {signal.why.map((reason, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-green-500 mt-1">•</span>
                          <span className="text-sm">{reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Warnings Section */}
                {signal.warnings && signal.warnings.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-bold text-sm uppercase tracking-wider text-amber-400 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      WARNINGS
                    </h4>
                    <ul className="space-y-2">
                      {signal.warnings.map((warning, index) => (
                        <li key={index} className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30">
                          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{warning}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* What Would Change */}
                {signal.what_would_change && (
                  <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30">
                    <div className="text-xs text-blue-400 mb-2">WHAT WOULD IMPROVE CONFIDENCE</div>
                    <p className="text-sm">{signal.what_would_change}</p>
                  </div>
                )}

                {/* AI Explanation */}
                {signal.explanation && (
                  <div className="p-4 bg-secondary/30 border border-border">
                    <div className="text-xs text-muted-foreground mb-2">AI ANALYSIS</div>
                    <p className="text-sm">{signal.explanation}</p>
                  </div>
                )}

                {/* User Decision Note */}
                <div className="mt-6 p-4 border border-dashed border-muted-foreground/30 text-center">
                  <p className="text-xs text-muted-foreground">
                    {signal.confidence >= 70 
                      ? "This signal meets high confidence criteria. Still, always manage your risk."
                      : signal.confidence >= 40
                        ? "This signal has warnings. Review carefully before deciding. Your call."
                        : "Low confidence signal. Consider waiting for better conditions, but the decision is yours."
                    }
                  </p>
                </div>
              </div>
            ) : (
              /* Empty State */
              <div className="bg-card border border-border p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-20 h-20 mx-auto bg-secondary flex items-center justify-center mb-6">
                    <TrendingUp className="w-10 h-10 text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Ready to Analyze</h3>
                  <p className="text-muted-foreground mb-6">
                    Select an instrument and timeframe, then click "Generate Signal" to receive a trade analysis with confidence level.
                  </p>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="p-3 bg-secondary/50 border border-border">
                      <div className="text-muted-foreground">Entry</div>
                      <div className="font-mono font-bold">—</div>
                    </div>
                    <div className="p-3 bg-secondary/50 border border-border">
                      <div className="text-muted-foreground">Stop Loss</div>
                      <div className="font-mono font-bold">—</div>
                    </div>
                    <div className="p-3 bg-secondary/50 border border-border">
                      <div className="text-muted-foreground">Take Profits</div>
                      <div className="font-mono font-bold">—</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 border-t border-border pt-6">
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            <span className="font-semibold text-amber-500/80">Disclaimer:</span> Aurex AI provides algorithmic market analysis, not financial advice. Trading decisions are the sole responsibility of the user. Aurex AI does not guarantee profits and is not liable for any losses.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
